import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FileText, Loader2, Send, Upload, HeartHandshake } from "lucide-react";

const openFile = async (url) => {
  try { const r = await api.get(url, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
  catch (e) { toast.error(apiError(e)); }
};

const NewReferral = ({ side, targets, onDone }) => {
  const [f, setF] = useState({ borrower_name: "", borrower_email: "", borrower_phone: "", to_name: "", to_email: "", notes: "",
    borrower_address: "", borrower_city: "", borrower_state: "", borrower_zip: "" });
  const [file, setFile] = useState(null);
  const [le, setLe] = useState(null);
  const [bba, setBba] = useState(null);
  const [sendBba, setSendBba] = useState(true);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    if (le) fd.append("loan_estimate", le);
    if (bba) { fd.append("bba", bba); fd.append("send_bba", sendBba && !!f.borrower_email); }
    try {
      const { data } = await api.post("/referrals", fd);
      toast.success(data.notified ? `Referral sent to ${f.to_name}` : `Referral saved. ${f.to_name} has no email on file, so the admin was notified.`);
      setF({ borrower_name: "", borrower_email: "", borrower_phone: "", to_name: "", to_email: "", notes: "", borrower_address: "", borrower_city: "", borrower_state: "", borrower_zip: "" });
      setFile(null); setLe(null); setBba(null); e.target.reset(); onDone();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const mlo = side === "mlo";
  return (
    <Card className="p-6" data-testid="new-referral-card">
      <h2 className="mb-4 font-display text-lg font-bold text-slate-900">{mlo ? "Refer a borrower to a realtor" : "Refer a client to a loan officer"}</h2>
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <div><Label>{mlo ? "Borrower's name" : "Client's name"}</Label><Input value={f.borrower_name} onChange={set("borrower_name")} required data-testid="ref-borrower-name" /></div>
        <div><Label>{mlo ? "Realtor" : "Loan officer"}</Label>
          <Select value={f.to_name} onValueChange={(v) => setF({ ...f, to_name: v })}>
            <SelectTrigger data-testid="ref-target-select"><SelectValue placeholder="Choose" /></SelectTrigger>
            <SelectContent>{targets.map((t) => <SelectItem key={t} value={t} data-testid={`ref-target-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="sm:col-span-2"><Label>{mlo ? "Realtor's email" : "Loan officer's email"} (used if they don't have an Elite Leads account yet)</Label>
          <Input type="email" value={f.to_email} onChange={set("to_email")} placeholder="name@example.com" data-testid="ref-target-email" /></div>
        <div><Label>Email{mlo ? "" : " (optional)"}</Label><Input type="email" value={f.borrower_email} onChange={set("borrower_email")} required={mlo} data-testid="ref-borrower-email" /></div>
        <div><Label>Phone (optional)</Label><Input value={f.borrower_phone} onChange={set("borrower_phone")} data-testid="ref-borrower-phone" /></div>
        {mlo && <div className="sm:col-span-2"><Label>Pre-qualification letter (PDF, JPG or PNG)</Label>
          <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0] || null)} required data-testid="ref-prequal-file" /></div>}
        {mlo && <div className="sm:col-span-2"><Label>Loan estimate (optional)</Label>
          <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setLe(e.target.files[0] || null)} data-testid="ref-le-file" /></div>}
        <>
          <div className="sm:col-span-2"><Label>{mlo ? "Borrower's mailing address (the pre-qual letter is mailed certified, signature required)" : "Client's address"}</Label>
            <Input value={f.borrower_address} onChange={set("borrower_address")} placeholder="Street" data-testid="ref-addr-street" /></div>
          <div className="grid grid-cols-3 gap-2 sm:col-span-2">
            <Input value={f.borrower_city} onChange={set("borrower_city")} placeholder="City" data-testid="ref-addr-city" />
            <Input value={f.borrower_state} onChange={set("borrower_state")} placeholder="State" maxLength={2} data-testid="ref-addr-state" />
            <Input value={f.borrower_zip} onChange={set("borrower_zip")} placeholder="ZIP" data-testid="ref-addr-zip" />
          </div>
        </>
        {!mlo && <div className="sm:col-span-2"><Label>Buyer-brokerage agreement (optional)</Label>
          <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setBba(e.target.files[0] || null)} data-testid="ref-bba-new-file" />
          {bba && <label className="mt-2 flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={sendBba} onCheckedChange={(v) => setSendBba(!!v)} data-testid="ref-bba-new-send" />
            Email it to the buyer to e-sign now (needs the client's email)</label>}</div>}
        <div className="sm:col-span-2"><Label>Notes (optional)</Label><Textarea value={f.notes} onChange={set("notes")} rows={2} data-testid="ref-notes" /></div>
        <div className="sm:col-span-2 flex justify-end">
          <Button type="submit" disabled={busy || !f.to_name} className="gold-cta border-0 font-bold" data-testid="ref-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" />Send referral</>}</Button>
        </div>
      </form>
    </Card>
  );
};

const DocChecklist = ({ r, onChange }) => {
  const toggle = async (key, received) => {
    try { onChange((await api.put(`/referrals/${r.id}/docs`, { key, received })).data); } catch (e) { toast.error(apiError(e)); }
  };
  const follow = async (active) => {
    try { onChange((await api.put(`/referrals/${r.id}/followup`, { active })).data); toast.success(active ? "Weekly emails on" : "Weekly emails paused"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`ref-docs-${r.id}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Income documents received</div>
        <label className="flex items-center gap-2 text-xs text-slate-700">Borrower outreach (weekly email, daily calls &amp; texts)
          <Switch checked={r.followup_active} onCheckedChange={follow} disabled={!r.prequal || r.contact_stopped} data-testid={`ref-followup-switch-${r.id}`} /></label>
      </div>
      {r.docs.map((d) => (
        <label key={d.key} className="flex items-start gap-2 py-1 text-sm text-slate-700">
          <Checkbox checked={d.received} onCheckedChange={(v) => toggle(d.key, !!v)} className="mt-0.5" data-testid={`ref-doc-${d.key}-${r.id}`} />
          <span className={d.received ? "text-slate-400 line-through" : ""}>{d.label}</span>
          {r.uploads.filter((u) => u.key === d.key).map((u) => (
            <button key={u.i} type="button" onClick={(e) => { e.preventDefault(); openFile(`/referrals/${r.id}/uploads/${u.i}`); }}
              className="ml-1 text-xs font-semibold text-amber-700 underline" data-testid={`ref-upload-${r.id}-${u.i}`}>{u.filename}</button>))}
        </label>
      ))}
      <div className="mt-2 text-xs text-slate-500" data-testid={`ref-followup-status-${r.id}`}>
        {r.contact_stopped ? `Contact stopped for good: ${r.stop_reason}.` : r.docs.every((d) => d.received) ? "All documents received - outreach stopped."
          : `${r.followups_sent} weekly emails · ${r.daily_count} days of calls & texts${r.daily_result ? ` (last: ${r.daily_result})` : ""}. Only unchecked documents are requested.`}
        {r.last_result && <div className="mt-1 font-semibold text-emerald-700">{r.last_result}</div>}
      </div>
      <MailAndLe r={r} onChange={onChange} />
    </div>
  );
};

const MailAndLe = ({ r, onChange }) => {
  const upLe = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { onChange((await api.post(`/referrals/${r.id}/le`, fd)).data); toast.success("Loan estimate added"); } catch (e) { toast.error(apiError(e)); }
  };
  const m = r.mail;
  const refresh = async () => {
    try { onChange((await api.post(`/referrals/${r.id}/mail/refresh`)).data); } catch (e) { toast.error(apiError(e)); }
  };
  const STATUS = { ready: "Received by PostGrid", printing: "Printing", processed_for_delivery: "With USPS", completed: "Mailed", cancelled: "Cancelled" };
  return (
    <div className="mt-3 space-y-2 border-t border-slate-200 pt-3 text-xs text-slate-600">
      <div data-testid={`ref-mail-status-${r.id}`}>Mailed pre-qual letter: {m?.error ? <span className="text-rose-600">failed - {m.error}</span>
        : m?.status ? <>
          <b>{m.delivered_at ? `Delivered ${new Date(m.delivered_at).toLocaleDateString()}` : (m.carrier_status || STATUS[m.status] || m.status).replace(/_/g, " ")}</b> · {m.service} · ordered {new Date(m.at).toLocaleDateString()}
          {m.send_date && ` · mails ${new Date(m.send_date).toLocaleDateString()}`}
          {m.tracking ? <> · USPS tracking <a href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${m.tracking}`} target="_blank" rel="noreferrer"
            className="font-semibold text-amber-700 underline" data-testid={`ref-mail-tracking-${r.id}`}>{m.tracking}</a></> : " · tracking number appears once USPS has it"}
          {" "}<button type="button" onClick={refresh} className="font-semibold text-amber-700 underline" data-testid={`ref-mail-refresh-${r.id}`}>Refresh</button>
        </> : r.borrower_address ? "not sent yet" : "add the borrower's address above to send it"}</div>
      <div className="flex flex-wrap items-center gap-2">
        Loan estimate: {r.loan_estimate ? <Button size="sm" variant="ghost" onClick={() => openFile(`/referrals/${r.id}/le`)} data-testid={`ref-le-view-${r.id}`}><FileText className="mr-1 h-4 w-4" />{r.loan_estimate.filename}</Button> : "none yet"}
        <label className="cursor-pointer font-semibold text-amber-700 underline">
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { upLe(e.target.files[0]); e.target.value = ""; }} data-testid={`ref-le-upload-${r.id}`} />
          {r.loan_estimate ? "Replace" : "Upload"}</label>
      </div>
    </div>
  );
};

const AddPrequal = ({ r, onChange }) => {
  const [file, setFile] = useState(null);
  const [email, setEmail] = useState(r.borrower_email);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("borrower_email", email);
    try { onChange((await api.post(`/referrals/${r.id}/prequal`, fd)).data); toast.success("Pre-qual letter sent to the borrower"); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-4 flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-slate-300 p-3" data-testid={`ref-add-prequal-${r.id}`}>
      <div className="min-w-[180px] flex-1"><Label className="text-xs">Borrower email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`ref-prequal-email-${r.id}`} /></div>
      <div className="min-w-[180px] flex-1"><Label className="text-xs">Pre-qual letter</Label><Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0] || null)} data-testid={`ref-prequal-upload-${r.id}`} /></div>
      <Button onClick={go} disabled={busy || !file} size="sm" data-testid={`ref-prequal-submit-${r.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send & start weekly emails"}</Button>
    </div>
  );
};

const BuyerAgreement = ({ r, onChange }) => {
  const [email, setEmail] = useState(r.borrower_email);
  const [busy, setBusy] = useState("");
  const b = r.bba;
  const upload = async (file) => {
    if (!file) return;
    setBusy("up");
    const fd = new FormData(); fd.append("file", file);
    try { onChange((await api.post(`/referrals/${r.id}/bba`, fd)).data); toast.success("Agreement uploaded"); } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };
  const send = async () => {
    setBusy("send");
    try { onChange((await api.post(`/referrals/${r.id}/bba/send`, { email })).data); toast.success("Sent for e-signature"); } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`ref-bba-${r.id}`}>
      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-600">Buyer-brokerage agreement</div>
      {b?.signed_at ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Badge className="border-0 bg-emerald-50 text-emerald-800" data-testid={`ref-bba-signed-${r.id}`}>Signed by {b.signed_name} · {new Date(b.signed_at).toLocaleString()}</Badge>
          <Button size="sm" variant="outline" onClick={() => openFile(`/referrals/${r.id}/bba`)} data-testid={`ref-bba-view-${r.id}`}>View agreement</Button>
          <Button size="sm" variant="outline" onClick={() => openFile(`/referrals/${r.id}/bba/signature.png`)} data-testid={`ref-bba-sig-${r.id}`}>View signature</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <label className="cursor-pointer">
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid={`ref-bba-file-${r.id}`} />
            <span className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-100">
              {busy === "up" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" />{b ? "Replace" : "Upload"}</>}</span>
          </label>
          {b && <>
            <Button size="sm" variant="ghost" onClick={() => openFile(`/referrals/${r.id}/bba`)} data-testid={`ref-bba-view-${r.id}`}><FileText className="mr-1 h-4 w-4" />{b.filename}</Button>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Buyer email" className="w-56" data-testid={`ref-bba-email-${r.id}`} />
            <Button size="sm" onClick={send} disabled={busy === "send"} className="gold-cta border-0 font-bold" data-testid={`ref-bba-send-${r.id}`}>
              {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : b.sent_at ? "Resend to e-sign" : "Email to e-sign"}</Button>
            {b.sent_at && <span className="text-xs text-slate-500" data-testid={`ref-bba-sent-${r.id}`}>Sent {new Date(b.sent_at).toLocaleDateString()} · waiting for signature</span>}
          </>}
        </div>
      )}
    </div>
  );
};

const AddressLine = ({ r, onChange }) => {
  const [edit, setEdit] = useState(false);
  const [a, setA] = useState({ address: "", city: "", state: "", zip: "" });
  const save = async () => {
    try { onChange((await api.put(`/referrals/${r.id}/address`, a)).data); setEdit(false); toast.success("Address saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!edit) return (
    <div className="text-xs text-slate-500" data-testid={`ref-address-${r.id}`}>
      {r.borrower_address || "No address yet"}{" "}
      <button type="button" onClick={() => setEdit(true)} className="font-semibold text-amber-700 underline" data-testid={`ref-address-edit-${r.id}`}>{r.borrower_address ? "Edit" : "Add address"}</button>
    </div>);
  return (
    <div className="mt-1 flex flex-wrap gap-2">
      {["address", "city", "state", "zip"].map((k) => <Input key={k} value={a[k]} onChange={(e) => setA({ ...a, [k]: e.target.value })} placeholder={k === "address" ? "Street" : k[0].toUpperCase() + k.slice(1)}
        className={k === "address" ? "min-w-[180px] flex-1" : "w-24"} data-testid={`ref-address-${k}-${r.id}`} />)}
      <Button size="sm" onClick={save} data-testid={`ref-address-save-${r.id}`}>Save</Button>
      <Button size="sm" variant="ghost" onClick={() => setEdit(false)}>Cancel</Button>
    </div>);
};

const Reassign = ({ r, accounts, onChange }) => {
  const opts = accounts.filter((a) => a.side === r.to_side && a.id !== r.to_id);
  const go = async (user_id) => {
    try { onChange((await api.put(`/referrals/${r.id}/reassign`, { user_id })).data); toast.success("Referral reassigned"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Select value="" onValueChange={go}>
      <SelectTrigger className="h-8 w-48 text-xs" data-testid={`ref-reassign-${r.id}`}><SelectValue placeholder="Reassign to..." /></SelectTrigger>
      <SelectContent>{opts.length ? opts.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`ref-reassign-opt-${a.id}`}>{a.name} ({a.email})</SelectItem>)
        : <div className="px-3 py-2 text-xs text-slate-500">No other {r.to_side === "realtor" ? "realtor" : "loan officer"} accounts yet</div>}</SelectContent>
    </Select>
  );
};

const ReferralCard = ({ r, onChange, accounts }) => (
  <Card className="p-5" data-testid={`referral-${r.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="font-display text-lg font-bold text-slate-900">{r.borrower_name}</div>
        <div className="text-sm text-slate-600">{r.incoming ? `From ${r.from_name}` : `${r.from_name} → ${r.to_name}`} · {new Date(r.created_at).toLocaleDateString()}</div>
        <div className="text-xs text-slate-500">{[r.borrower_email, r.borrower_phone].filter(Boolean).join(" · ")}</div>
        <AddressLine r={r} onChange={onChange} />
        {r.notes && <div className="mt-1 text-sm text-slate-700">{r.notes}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={`border-0 ${r.incoming ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{r.incoming ? "Received" : "Sent"}</Badge>
        {!r.incoming && !r.to_has_account && <Badge className="border-0 bg-rose-50 text-rose-700" data-testid={`ref-waiting-${r.id}`}>Waiting for account</Badge>}
        {r.prequal && <Button size="sm" variant="outline" onClick={() => openFile(`/referrals/${r.id}/prequal`)} data-testid={`ref-prequal-view-${r.id}`}><FileText className="mr-1 h-4 w-4" />Pre-qual</Button>}
        {accounts && <Reassign r={r} accounts={accounts} onChange={onChange} />}
      </div>
    </div>
    {r.is_mlo && (r.prequal ? <DocChecklist r={r} onChange={onChange} /> : <AddPrequal r={r} onChange={onChange} />)}
    {r.is_realtor && <BuyerAgreement r={r} onChange={onChange} />}
  </Card>
);

export default function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const load = useCallback(() => { api.get("/referrals").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user?.role === "admin") api.get("/referrals/accounts").then((r) => setAccounts(r.data.accounts)).catch(() => {});
  }, [user]);
  const update = (row) => setData((d) => ({ ...d, referrals: d.referrals.map((x) => (x.id === row.id ? row : x)) }));
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="referrals-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold text-slate-900"><HeartHandshake className="h-7 w-7 text-amber-500" />Referrals</h1>
        <p className="text-sm text-slate-600">{data.side === "mlo"
          ? "Refer pre-qualified borrowers to a realtor. Ava mails the pre-qual letter (certified), emails the borrower weekly, and calls and texts daily for the income documents still needed."
          : "Exchange referrals with loan officers and send buyer-brokerage agreements for e-signature."}
          {" "}No compensation may be offered or exchanged for referrals (RESPA Section 8).</p>
      </div>
      <NewReferral side={data.side} targets={data.targets} onDone={load} />
      <div className="space-y-4" data-testid="referrals-list">
        {data.referrals.length ? data.referrals.map((r) => <ReferralCard key={r.id} r={r} onChange={update} accounts={accounts} />)
          : <p className="py-8 text-center text-sm text-slate-500" data-testid="referrals-empty">No referrals yet.</p>}
      </div>
    </div>
  );
}
